





















































import { Component, Inject, Prop, Watch } from "vue-property-decorator";
import { mixins } from "vue-class-component";
import { getModule } from "vuex-module-decorators";

import FormErrors from "@/lib/components/FormErrors.vue";
import FormField from "@/lib/components/FormField.vue";
import LoadingSpinner from "@/lib/components/LoadingSpinner.vue";
import SubmitButton from "@/lib/components/SubmitButton.vue";

import GeneralFormMixin from "@/lib/mixins/GeneralForm";
import IdGenerator from "@/lib/services/id-generator";
import dataStore from "@/store";
import { Resource } from "@/modules/account/resource";
import AuthenticatedUserProvider from "@/modules/account/authenticated-user-provider.service";

import FactoryPhaseSupport from "../factory-phase-support.model";
import { ProductAllocationSlotTypeValue } from "../product-allication-slot-type.value";
import FactoryStore from "../store";

@Component({
  components: {
    FormErrors,
    FormField,
    LoadingSpinner,
    SubmitButton,
  },
})
export default class FactoryPhaseSupportForm extends mixins(GeneralFormMixin) {
  @Prop({ required: true })
  public readonly factoryId!: string;

  @Inject("AuthenticatedUserProvider")
  private fUserProvider!: AuthenticatedUserProvider;

  @Inject("IdGenerator")
  private fIdGenerator!: IdGenerator;

  public designSupported = false;
  public productionSupported = false;
  public loadErrors: string[] = [];

  private fFactoryDataStore: FactoryStore;
  private fIsLoading = false;

  get canManage(): boolean {
    const user = this.fUserProvider.getUser();

    if (!user) {
      return false;
    }

    return user.hasPermissionForResource(Resource.FACTORY_PHASE_SUPPORT_MANAGE);
  }

  get canView(): boolean {
    const user = this.fUserProvider.getUser();

    if (!user) {
      return false;
    }

    return (
      this.canManage ||
      user.hasPermissionForResource(Resource.FACTORY_PHASE_SUPPORT_VIEW)
    );
  }

  get isLoading(): boolean {
    return this.fIsLoading;
  }

  constructor() {
    super();
    this.fFactoryDataStore = getModule(FactoryStore, dataStore);
  }

  protected async performSubmit(): Promise<void> {
    try {
      await this.persistSlot(
        ProductAllocationSlotTypeValue.DESIGN,
        this.designSupported
      );
      await this.persistSlot(
        ProductAllocationSlotTypeValue.PRODUCTION,
        this.productionSupported
      );
    } catch (error) {
      await this.loadPhaseSupports(false);
      throw error;
    }

    this.fillValuesFromStore();
  }

  private async persistSlot(
    slotType: ProductAllocationSlotTypeValue,
    isSupported: boolean
  ): Promise<void> {
    const isCurrentlySupported = this.fFactoryDataStore.getIsFactoryPhaseSupported(
      this.factoryId,
      slotType
    );

    if (isSupported === isCurrentlySupported) {
      return;
    }

    if (isSupported) {
      await this.fFactoryDataStore.enableFactoryPhaseSupport(
        new FactoryPhaseSupport(
          this.fIdGenerator.getId(),
          this.factoryId,
          slotType
        )
      );
      return;
    }

    await this.fFactoryDataStore.disableFactoryPhaseSupport({
      factoryId: this.factoryId,
      slotType,
    });
  }

  private fillValuesFromStore(): void {
    this.designSupported = this.fFactoryDataStore.getIsFactoryPhaseSupported(
      this.factoryId,
      ProductAllocationSlotTypeValue.DESIGN
    );
    this.productionSupported = this.fFactoryDataStore.getIsFactoryPhaseSupported(
      this.factoryId,
      ProductAllocationSlotTypeValue.PRODUCTION
    );
  }

  private async loadPhaseSupports(useCache: boolean): Promise<void> {
    if (!this.canView) {
      return;
    }

    this.fIsLoading = true;
    this.loadErrors = [];

    try {
      await this.fFactoryDataStore.loadFactoryPhaseSupportsByFactoryId({
        factoryId: this.factoryId,
        useCache,
      });
      this.fillValuesFromStore();
    } catch (error) {
      this.designSupported = false;
      this.productionSupported = false;
      this.loadErrors = this.fErrorConverterService.describeError(error);
    } finally {
      this.fIsLoading = false;
    }
  }

  @Watch("factoryId", { immediate: true })
  private async _onFactoryIdChange(): Promise<void> {
    if (!this.factoryId) {
      return;
    }

    await this.loadPhaseSupports(false);
  }
}
